import {classifyLatencyTier} from './thresholds'
import type {LatencyEndpointResult, LatencySample} from './types'

/** Matches Amazon Connect Endpoint Test Utility: warmup discarded + 10 measured. */
export const DEFAULT_LATENCY_ITERATIONS = 10

/**
 * Single timed fetch. Uses mode: 'no-cors' so opaque/403 responses still succeed —
 * the metric is round-trip time, not HTTP status (same as AWS tool).
 */
export const measureLatencySample = async (endpointUrl: string): Promise<LatencySample> => {
  const item: LatencySample = {
    start: 0,
    end: 0,
    latency: 0,
    success: true,
    error: null,
  }

  item.start = Date.now()
  try {
    const headers = new Headers()
    // AWS tool typo "no-cahche" is intentional parity for pragma; cache-control is correct.
    headers.append('pragma', 'no-cache')
    headers.append('cache-control', 'no-cache')
    await fetch(endpointUrl, {
      mode: 'no-cors',
      redirect: 'follow',
      headers,
      cache: 'no-store',
    })
    item.success = true
    item.error = null
  } catch (err) {
    item.success = false
    item.error = err instanceof Error ? err.toString() : String(err)
  }
  item.end = Date.now()
  item.latency = item.end - item.start
  return item
}

export const measureLatencyEndpoint = async (
  endpointUrl: string,
  meta: {name: string; code: string},
  iterations: number = DEFAULT_LATENCY_ITERATIONS
): Promise<LatencyEndpointResult> => {
  // Warmup (discarded) — primes DNS/TLS/cache path like the AWS tool.
  await measureLatencySample(endpointUrl)

  const latencyItems: LatencySample[] = []
  for (let i = 0; i < iterations; i++) {
    latencyItems.push(await measureLatencySample(endpointUrl))
  }

  const succeeded = latencyItems.filter((s) => s.success)
  const allOk = latencyItems.length > 0 && succeeded.length === latencyItems.length
  const latencies = (allOk ? latencyItems : succeeded).map((s) => s.latency)

  const minLatency = latencies.length ? Math.round(Math.min(...latencies)) : 0
  const maxLatency = latencies.length ? Math.round(Math.max(...latencies)) : 0
  const averageLatency = latencies.length
    ? Math.round(latencies.reduce((sum, n) => sum + n, 0) / latencies.length)
    : 0

  return {
    name: meta.name,
    code: meta.code,
    endpoint: endpointUrl,
    success: allOk,
    minLatency,
    maxLatency,
    averageLatency,
    tier: classifyLatencyTier(averageLatency, allOk),
    latencyItems,
  }
}
